export const ALL_LOCALE_CODES = ['en', 'ru', 'fr', 'es', 'de', 'ja'] as const;
export const DEFAULT_LOCALE_CODE = 'en';

export type LocaleCode = typeof ALL_LOCALE_CODES[number];

export function isLocaleCode(value: string): value is LocaleCode {
  return ALL_LOCALE_CODES.includes(value as LocaleCode);
}

function normalizeLocaleCodes(values: unknown): LocaleCode[] {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.toLowerCase())
    .filter(isLocaleCode)
    .filter((value, index, array) => array.indexOf(value) === index);
}

export function parseExcludedLocales(raw: unknown): LocaleCode[] {
  if (Array.isArray(raw)) {
    return normalizeLocaleCodes(raw);
  }

  if (typeof raw !== 'string') {
    return [];
  }

  try {
    return normalizeLocaleCodes(JSON.parse(raw) as unknown);
  } catch {
    return [];
  }
}

export function getAllowedLocales(excluded: readonly LocaleCode[]): LocaleCode[] {
  const allowedLocales = ALL_LOCALE_CODES.filter(locale => !excluded.includes(locale));

  return allowedLocales.length > 0 ? allowedLocales : [...ALL_LOCALE_CODES];
}

export function getFallbackLocale(excluded: readonly LocaleCode[], preferred?: unknown): LocaleCode {
  const allowedLocales = getAllowedLocales(excluded);
  const normalized = typeof preferred === 'string' ? preferred.trim().toLowerCase() : '';

  if (isLocaleCode(normalized) && allowedLocales.includes(normalized)) {
    return normalized;
  }

  return allowedLocales.includes(DEFAULT_LOCALE_CODE)
    ? DEFAULT_LOCALE_CODE
    : allowedLocales[0] || DEFAULT_LOCALE_CODE;
}

export function isLocaleExcluded(locale: string, excluded: readonly LocaleCode[]): boolean {
  if (!isLocaleCode(locale)) {
    return false;
  }

  return !getAllowedLocales(excluded).includes(locale);
}

export function filterLocales<T extends { code: string }>(
  locales: readonly T[],
  excluded: readonly LocaleCode[],
): T[] {
  const allowedLocales = getAllowedLocales(excluded);

  return locales.filter(locale => allowedLocales.includes(locale.code as LocaleCode));
}
